import { Navigate, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Login from './pages/Login';
import Landing from './pages/Landing';
import Welcome from './pages/Welcome';
import MinimaxH3Video from './pages/MinimaxH3Video';
import VideoAgent from './pages/VideoAgent';
// LtxVideo and Wan22Video pages are hidden (all video moved to MiniMax H3);
// the files stay in pages/ and their old routes redirect below.
import ImageEdit from './pages/ImageEdit';
import MultiAngle from './pages/MultiAngle';
import FaceSwap from './pages/FaceSwap';
import TextToImage from './pages/TextToImage';
import HumanPortrait from './pages/HumanPortrait';
import InstagramGenerator from './pages/InstagramGenerator';
import GenerationHistory from './pages/GenerationHistory';
import Credits from './pages/Credits';
import Payment from './pages/Payment';
import ChangePassword from './pages/ChangePassword';
import RecoveryEmail from './pages/RecoveryEmail';
import ApiKeys from './pages/ApiKeys';
import Support from './pages/Support';
import Users from './pages/Users';
import Pricing from './pages/Pricing';
import VideoShares from './pages/VideoShares';
import SharedView from './pages/SharedView';
import Layout from './components/Layout';
import { useAuth } from './auth/AuthProvider';
import type { JSX } from 'react';

function FullScreenSpinner() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) {
    const returnTo = window.location.pathname + window.location.search;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <Layout>{children}</Layout>;
}

// Like RequireAuth, but also requires the global_admin role. Non-admins are
// bounced home rather than shown a 403 page.
function RequireGlobalAdmin({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) {
    const returnTo = window.location.pathname + window.location.search;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  if (user.role !== 'global_admin') return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

// `/` shows the marketing landing for visitors; signed-in users get the
// Welcome dashboard inside the standard Layout.
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (user) return <Layout><Welcome /></Layout>;
  return <Landing />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<HomeRoute />} />
      <Route path="/agent" element={<RequireAuth><VideoAgent /></RequireAuth>} />
      <Route path="/minimax-h3" element={<RequireAuth><MinimaxH3Video /></RequireAuth>} />
      <Route path="/ltx-video" element={<Navigate to="/minimax-h3" replace />} />
      <Route path="/wan22-video" element={<Navigate to="/minimax-h3" replace />} />
      {/* video-add-sound, ltx-video-extend and video-face-swap are hidden until
          they move to H3; their pages stay in pages/ and fall through to `*`. */}
      <Route path="/image-edit" element={<RequireAuth><ImageEdit /></RequireAuth>} />
      <Route path="/multi-angle" element={<RequireAuth><MultiAngle /></RequireAuth>} />
      <Route path="/face-swap" element={<RequireAuth><FaceSwap /></RequireAuth>} />
      <Route path="/text-to-image" element={<RequireAuth><TextToImage /></RequireAuth>} />
      <Route path="/human-portrait" element={<RequireAuth><HumanPortrait /></RequireAuth>} />
      <Route path="/instagram-generator" element={<RequireAuth><InstagramGenerator /></RequireAuth>} />
      <Route path="/history" element={<RequireAuth><GenerationHistory /></RequireAuth>} />
      <Route path="/credits" element={<RequireAuth><Credits /></RequireAuth>} />
      <Route path="/payment" element={<RequireAuth><Payment /></RequireAuth>} />
      <Route path="/account/password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
      <Route path="/account/recovery" element={<RequireAuth><RecoveryEmail /></RequireAuth>} />
      <Route path="/account/api-keys" element={<RequireAuth><ApiKeys /></RequireAuth>} />
      <Route path="/account" element={<Navigate to="/account/password" replace />} />
      <Route path="/support" element={<RequireAuth><Support /></RequireAuth>} />
      <Route path="/admin/users" element={<RequireGlobalAdmin><Users /></RequireGlobalAdmin>} />
      <Route path="/admin/pricing" element={<RequireGlobalAdmin><Pricing /></RequireGlobalAdmin>} />
      <Route path="/admin/shares" element={<RequireGlobalAdmin><VideoShares /></RequireGlobalAdmin>} />
      {/* Public video-share viewer: the token in the URL is the only credential. */}
      <Route path="/s/:token" element={<SharedView />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
